import { useEffect, useState } from "react";
import { Truck, KeyRound, Save, PlugZap, CheckCircle2, XCircle, RefreshCw, Warehouse } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { getSettings, saveSettings, testConnection, bootstrap, triggerSync } from "@/lib/delivery";
import { fmtDateTz } from "@/lib/format";

export default function DeliverySettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [working, setWorking] = useState(false);
  const [form, setForm] = useState({
    username: "",
    password: "",
    client_number: "",
    country: "ro",
    enabled: true,
  });
  const [meta, setMeta] = useState({});
  const [wh, setWh] = useState({
    name: "", contact_name: "", contact_phone: "", contact_email: "",
    street: "", house_number: "", city: "", zip_code: "", county: "", country: "RO",
  });
  const [savingWh, setSavingWh] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, w] = await Promise.all([
        getSettings(),
        api.get("/returns/warehouse-address").then((r) => r.data).catch(() => ({})),
      ]);
      setForm((f) => ({
        ...f,
        username: s.username || "",
        client_number: s.client_number ?? "",
        country: s.country || "ro",
        enabled: s.enabled ?? true,
        password: "",
      }));
      setMeta(s);
      if (w && (w.street || w.city)) setWh((cur) => ({ ...cur, ...w }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSave = async () => {
    if (!form.username || !form.client_number) {
      toast.error("Username și Client Number sunt obligatorii");
      return;
    }
    if (!meta.password_set && !form.password) {
      toast.error("Parola este obligatorie la prima configurare");
      return;
    }
    setSaving(true);
    try {
      await saveSettings({
        username: form.username,
        password: form.password,
        client_number: Number(form.client_number),
        country: form.country,
        enabled: form.enabled,
      });
      toast.success("Setări salvate");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await testConnection();
      setTestResult(r);
      if (r.ok) toast.success(r.message);
      else toast.error(r.message);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  const onBootstrap = async () => {
    setWorking(true);
    try {
      const r = await bootstrap(90);
      toast.success(`Bootstrap complet: ${r.created} colete create, ${r.existing} existau deja`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWorking(false);
    }
  };

  const onSyncNow = async () => {
    setWorking(true);
    try {
      const r = await triggerSync(30, true);
      toast.success(r.message || "Sync pornit");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="delivery-settings-page">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center">
          <Truck className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Setări Livrări — MyGLS</h1>
          <p className="text-sm text-muted-foreground">
            Credențialele MyGLS sunt criptate la rest. Sync-ul rulează automat la fiecare 30 minute pentru AWB-urile active.
          </p>
        </div>
      </div>

      <div className="bg-white border border-border rounded-xl p-6 space-y-5">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <KeyRound className="h-4 w-4 text-primary" /> Credențiale MyGLS
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="gls-username">Username</Label>
            <Input
              id="gls-username"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              placeholder="email@example.com"
              data-testid="gls-username"
            />
          </div>
          <div>
            <Label htmlFor="gls-password">
              Parolă {meta.password_set ? <span className="text-muted-foreground text-xs">(lasă gol pentru a păstra)</span> : null}
            </Label>
            <Input
              id="gls-password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder={meta.password_set ? "••••••••" : "Parola MyGLS"}
              data-testid="gls-password"
            />
          </div>
          <div>
            <Label htmlFor="gls-client">Client Number</Label>
            <Input
              id="gls-client"
              type="number"
              value={form.client_number}
              onChange={(e) => setForm({ ...form, client_number: e.target.value })}
              placeholder="553004427"
              data-testid="gls-client-number"
            />
          </div>
          <div>
            <Label htmlFor="gls-country">Țară API</Label>
            <Input
              id="gls-country"
              value={form.country}
              onChange={(e) => setForm({ ...form, country: e.target.value.toLowerCase() })}
              placeholder="ro"
              data-testid="gls-country"
            />
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-border pt-4">
          <div>
            <Label htmlFor="gls-enabled" className="text-sm">Sync automat activ</Label>
            <p className="text-xs text-muted-foreground">Pornește cron-ul de sync la fiecare 30 min</p>
          </div>
          <Switch
            id="gls-enabled"
            checked={form.enabled}
            onCheckedChange={(v) => setForm({ ...form, enabled: v })}
            data-testid="gls-enabled"
          />
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button onClick={onSave} disabled={saving || loading} data-testid="gls-save-btn">
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Se salvează…" : "Salvează"}
          </Button>
          <Button onClick={onTest} disabled={testing || loading} variant="outline" data-testid="gls-test-btn">
            <PlugZap className={`h-4 w-4 mr-2 ${testing ? "animate-pulse" : ""}`} />
            Test conexiune
          </Button>
          {testResult && (
            <Badge variant="outline" className={testResult.ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}>
              {testResult.ok ? <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> : <XCircle className="h-3.5 w-3.5 mr-1" />}
              {testResult.message}
            </Badge>
          )}
        </div>
      </div>

      <div className="bg-white border border-border rounded-xl p-6 space-y-4">
        <div className="text-sm font-semibold">Stare sincronizare</div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <Tile label="Ultimul sync" value={meta.last_sync_at ? fmtDateTz(meta.last_sync_at) : "—"} />
          <Tile label="Status" value={meta.last_sync_status || "—"} />
          <Tile label="Actualizat" value={meta.updated_at ? fmtDateTz(meta.updated_at) : "—"} />
        </div>
        {meta.last_sync_message && (
          <div className="text-xs text-muted-foreground border border-border bg-secondary/40 rounded-md p-3 font-mono">
            {meta.last_sync_message}
          </div>
        )}

        <div className="flex gap-2 pt-2 border-t border-border">
          <Button onClick={onBootstrap} disabled={working} variant="outline" size="sm" data-testid="gls-bootstrap-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${working ? "animate-spin" : ""}`} />
            Import AWB-uri din Shopify (90 zile)
          </Button>
          <Button onClick={onSyncNow} disabled={working || !meta.password_set} size="sm" data-testid="gls-sync-now-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${working ? "animate-spin" : ""}`} />
            Sincronizează acum
          </Button>
        </div>
      </div>

      {/* Warehouse address card */}
      <div className="bg-white border border-border rounded-xl p-6 space-y-5" data-testid="warehouse-address-card">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Warehouse className="h-4 w-4 text-primary" />
          Adresa depozitului IEFTINIT
          <span className="text-xs font-normal text-muted-foreground ml-1">(destinația AWB-urilor retur de la clienți)</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label className="text-xs">Nume companie / locație</Label>
            <Input value={wh.name} onChange={(e) => setWh({ ...wh, name: e.target.value })} placeholder="IEFTINIT.RO Depozit" data-testid="wh-name" />
          </div>
          <div>
            <Label className="text-xs">Persoană de contact</Label>
            <Input value={wh.contact_name} onChange={(e) => setWh({ ...wh, contact_name: e.target.value })} placeholder="Depozit IEFTINIT" data-testid="wh-contact-name" />
          </div>
          <div>
            <Label className="text-xs">Telefon contact</Label>
            <Input value={wh.contact_phone} onChange={(e) => setWh({ ...wh, contact_phone: e.target.value })} placeholder="+40700000000" data-testid="wh-contact-phone" />
          </div>
          <div>
            <Label className="text-xs">Email contact</Label>
            <Input value={wh.contact_email || ""} onChange={(e) => setWh({ ...wh, contact_email: e.target.value })} placeholder="depozit@ieftinit.ro" data-testid="wh-contact-email" />
          </div>
          <div>
            <Label className="text-xs">Județ</Label>
            <Input value={wh.county || ""} onChange={(e) => setWh({ ...wh, county: e.target.value })} placeholder="B / IF / CJ / ..." data-testid="wh-county" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Stradă</Label>
            <Input value={wh.street} onChange={(e) => setWh({ ...wh, street: e.target.value })} placeholder="Bulevardul Iuliu Maniu" data-testid="wh-street" />
          </div>
          <div>
            <Label className="text-xs">Număr</Label>
            <Input value={wh.house_number || ""} onChange={(e) => setWh({ ...wh, house_number: e.target.value })} placeholder="78" data-testid="wh-house-number" />
          </div>
          <div>
            <Label className="text-xs">Oraș</Label>
            <Input value={wh.city} onChange={(e) => setWh({ ...wh, city: e.target.value })} placeholder="București" data-testid="wh-city" />
          </div>
          <div>
            <Label className="text-xs">Cod poștal</Label>
            <Input value={wh.zip_code} onChange={(e) => setWh({ ...wh, zip_code: e.target.value })} placeholder="061104" data-testid="wh-zip" />
          </div>
          <div>
            <Label className="text-xs">Țară</Label>
            <Input value={wh.country || "RO"} onChange={(e) => setWh({ ...wh, country: e.target.value.toUpperCase() })} placeholder="RO" data-testid="wh-country" />
          </div>
        </div>

        <Button
          onClick={async () => {
            if (!wh.street || !wh.city || !wh.zip_code) {
              toast.error("Strada, orașul și codul poștal sunt obligatorii");
              return;
            }
            setSavingWh(true);
            try {
              await api.put("/returns/warehouse-address", wh);
              toast.success("Adresa depozitului salvată");
            } catch (e) {
              toast.error(formatApiError(e));
            } finally {
              setSavingWh(false);
            }
          }}
          disabled={savingWh}
          data-testid="wh-save-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          {savingWh ? "Se salvează…" : "Salvează adresa depozit"}
        </Button>
      </div>
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="bg-secondary/40 rounded-md px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className="text-sm font-medium mt-0.5">{value}</div>
    </div>
  );
}
